import { useCallback, useEffect, useState } from 'react';
import type { Tenant, TenantStatus } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function statusVariant(status: TenantStatus): 'success' | 'warning' | 'secondary' {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'warning';
  return 'secondary';
}

export function TenantsPage() {
  const { tenantId, setTenant, user, access } = useApp();
  const isAdmin = user.isOrqForgeAdmin || !!access?.isOrqForgeAdmin;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = isAdmin ? await apiClient.listAllTenants() : await apiClient.listMyTenants();
      setTenants(items);
      setError(null);
      if (!tenantId && items.length > 0) setTenant(items[0].id);
    } catch (err) {
      setError(errText(err, 'Failed to load tenants'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, tenantId, setTenant]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleCreate() {
    setError(null);
    setName('');
    setSlug('');
    setCreating((v) => !v);
  }

  async function create() {
    setError(null);
    setLoading(true);
    try {
      const tenant = await apiClient.createTenant({ name, slug });
      setName('');
      setSlug('');
      setCreating(false);
      setTenant(tenant.id);
    } catch (err) {
      setError(errText(err, 'Failed to create tenant'));
      setLoading(false);
    }
  }

  async function setStatus(tenant: Tenant, status: string) {
    try {
      await apiClient.updateTenant(tenant.id, { status });
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to update tenant'));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tenants</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage tenants. Each tenant has its own workspaces, agents, and data.
          </p>
        </div>
        <Button size="sm" onClick={toggleCreate}>
          New tenant
        </Button>
      </div>

      {creating ? (
        <Card className="mt-4">
          <CardContent className="pt-6">
            <h2 className="mb-2 text-lg font-semibold">Create a new tenant</h2>
            <div className="grid max-w-xl gap-3">
              <label className="grid gap-1.5 text-sm">
                <span>Tenant name</span>
                <Input
                  placeholder="Company name (e.g. Acme Corp)"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setSlug(slugify(e.target.value));
                  }}
                />
              </label>
              {slug ? (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>
                    Slug: <code className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800">{slug}</code>
                  </span>
                </div>
              ) : null}
              {error ? (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
                  {error}
                </div>
              ) : null}
              <div className="flex gap-2">
                <Button disabled={!name || !slug || loading} onClick={() => void create()}>
                  {loading ? 'Creating...' : 'Create tenant'}
                </Button>
                <Button variant="ghost" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!creating && error ? (
        <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {loading && !creating ? (
        <div className="mt-4 text-sm text-gray-500">Loading tenants...</div>
      ) : null}

      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
        {tenants.map((t) => (
          <Card key={t.id} className={t.id === tenantId ? 'ring-2 ring-blue-600' : undefined}>
            <CardContent className="grid gap-3 pt-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <strong>{t.name}</strong>
                  <div className="text-xs text-gray-500">{t.slug}</div>
                </div>
                <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
              </div>
              <div className="text-xs text-gray-500">
                {t.isolationMode}
                {t.keycloakRealm ? <span className="opacity-70"> · realm: {t.keycloakRealm}</span> : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {t.id !== tenantId && t.status !== 'suspended' ? (
                  <Button size="sm" variant="outline" onClick={() => setTenant(t.id)}>
                    Switch
                  </Button>
                ) : t.id === tenantId ? (
                  <Badge>active</Badge>
                ) : null}
                {isAdmin && t.status === 'active' ? (
                  <Button size="sm" variant="ghost" onClick={() => void setStatus(t, 'suspended')}>
                    Suspend
                  </Button>
                ) : null}
                {isAdmin && t.status === 'suspended' ? (
                  <Button size="sm" variant="ghost" onClick={() => void setStatus(t, 'active')}>
                    Reactivate
                  </Button>
                ) : null}
                {isAdmin ? (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => void setStatus(t, 'deleted')}>
                    Delete
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
        {!loading && tenants.length === 0 ? (
          <p className="col-span-full text-sm text-gray-500">
            No tenants yet. Click &quot;New tenant&quot; above to create one.
          </p>
        ) : null}
      </div>
    </div>
  );
}
